import { Injectable } from '@angular/core';

import { CopilotBackgroundCheckRunner } from './copilot-background-check-runner';
import { CopilotContextService, CopilotPageContextProvider } from './copilot-context.service';
import { CopilotNotificationsService } from './copilot-notifications.service';

/**
 * Runs the background checks pages declare (`provider.backgroundCheck`).
 *
 * A page registering with Tailor gets a runner for its check; the page's
 * content changes drive it, Tailor's panel opening pauses it, and what it
 * finds is raised as a notification on that page. The page going away ends
 * it. Pages never see any of this — they only say what to check and when
 * their content changed.
 */
@Injectable({ providedIn: 'root' })
export class CopilotBackgroundChecksService {
  private readonly runners = new Map<CopilotPageContextProvider, CopilotBackgroundCheckRunner>();
  private panelOpen = false;
  private started = false;

  constructor(
    private copilotContext: CopilotContextService,
    private copilotNotifications: CopilotNotificationsService,
  ) {}

  /** Called once by the app shell, before any page registers. */
  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.copilotContext.panelOpen$.subscribe((open) => {
      this.panelOpen = open;
      this.runners.forEach((runner) => runner.setPaused(open));
    });
    this.copilotContext.providerRegistered$.subscribe((owner) => this.add(owner));
    this.copilotContext.providerUnregistered$.subscribe((owner) => {
      this.runners.get(owner)?.destroy();
      this.runners.delete(owner);
    });
    this.copilotContext.contentChanges$.subscribe(({ provider, kind }) => {
      const runner = this.runners.get(provider);

      if (kind === 'changed') {
        runner?.changed();
      } else {
        runner?.settled();
      }
    });
  }

  private add(owner: CopilotPageContextProvider) {
    if (!owner.backgroundCheck || this.runners.has(owner)) {
      return;
    }

    const runner = new CopilotBackgroundCheckRunner(
      owner.backgroundCheck,
      (notification) => this.copilotNotifications.raise(owner, notification),
      () => this.copilotNotifications.all(owner),
    );

    runner.setPaused(this.panelOpen);
    this.runners.set(owner, runner);
  }
}
